/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ButtonClasses,
  ComponentsOverrides,
  ComponentsProps,
  ComponentsVariants,
  Theme,
  alpha,
} from '@mui/material';
import { OverridesStyleRules } from '@mui/material/styles/overrides';
import { COLORS } from '@/config/colors';
import { desaturate } from '@/utils/color';

export const button: {
  defaultProps?: ComponentsProps['MuiButton'];
  styleOverrides?: ComponentsOverrides<Theme>['MuiButton'];
  variants?: ComponentsVariants<Theme>['MuiButton'];
} = {
  defaultProps: {
    color: 'primary',
    variant: 'contained',
    disableElevation: true,
    size: 'medium',
  },
  styleOverrides: {
    root({ theme, ownerState }) {
      const props: Partial<
        OverridesStyleRules<keyof ButtonClasses, 'MuiButton', Theme>
      >['root'] = {};
      if (ownerState.color && ownerState.color !== 'inherit') {
        if (ownerState.variant === 'contained') {
          props.border = 'none';
          props.background = COLORS[ownerState.color][500];
          props.color = COLORS.grey[50];

          props[':hover'] = {
            ...(props[':hover'] as any),
            background: COLORS[ownerState.color][800],
            color: COLORS.grey[100],
          };
          props[':disabled'] = {
            background: COLORS[ownerState.color][500],
            color: COLORS.grey[50],
            opacity: 0.5,
            cursor: 'not-allowed',
            pointerEvents: 'auto',
          };
        } else if (ownerState.variant === 'outlined') {
          props.borderColor = theme.palette[ownerState.color].main;
          props.background = 'transparent';
          props[':hover'] = {
            ...(props[':hover'] as any),
            background: alpha(theme.palette[ownerState.color].main, 0.3),
          };
        } else if (ownerState.variant === 'text') {
          props[':hover'] = {
            ...(props[':hover'] as any),
            background: alpha(theme.palette[ownerState.color].main, 0.2),
          };
        }
      }
      if (ownerState.disabled) {
        props.borderColor =
          typeof props.borderColor === 'string'
            ? alpha(desaturate(props.borderColor, 1), 0.2)
            : 'transparent';
        props.background = 'red';
      }
      if (ownerState.size === 'small') {
        props.padding = '0.25rem 0.625rem';
        props.minHeight = 27;
        props.fontSize = 11;
        props.lineHeight = 1.15;
        props.fontWeight = 500;
      } else if (ownerState.size === 'large') {
        props.padding = '0.375rem 0.9375rem';
        props.minHeight = 33;
        props.fontSize = 13;
        props.lineHeight = 1.2;
        props.fontWeight = 500;
      } else {
        props.padding = '0.3125rem 0.75rem';
        props.minHeight = 30;
        props.fontSize = 12;
        props.lineHeight = 1.15;
        props.fontWeight = 500;
      }
      // Ces hauteurs — 27, 30, 33 — sont des densités de bureau : elles se
      // pointent à la souris au pixel près. Au pouce, dont la surface de
      // contact fait environ 9mm, elles se ratent. Le bouton-icône avait déjà
      // son plancher (voir icon-button.ts) ; le bouton avec libellé n'en avait
      // aucun, ce qui laissait des « Rejoindre » de 27px de haut sur toutes les
      // listes. On relève le plancher sous md sans toucher au dessin.
      (props as any)[theme.breakpoints.down('md')] = {
        minHeight: ownerState.size === 'small' ? 36 : 40,
        fontSize: ownerState.size === 'small' ? 12 : 13,
      };
      if (ownerState.variant === 'contained' || ownerState.variant === 'outlined') {
        props.boxShadow =
          'inset 0 1px 0 rgba(255,255,255,0.10), 0 1px 0 rgba(0,0,0,0.40)';
        (props as any)[':active'] = {
          ...((props as any)[':active'] as any),
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.45)',
          transform: 'translateY(0.5px)',
        };
      }
      if (ownerState.variant === 'text') {
        props.background = '#131318';
        props.border = '1px solid rgba(255,255,255,0.05)';
        props.boxShadow =
          'inset 0 1px 0 rgba(255,255,255,0.05), 0 1px 0 rgba(0,0,0,0.35)';
        (props as any)[':hover'] = {
          ...((props as any)[':hover'] as any),
          background: '#1f1f28',
        };
        (props as any)[':active'] = {
          ...((props as any)[':active'] as any),
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.40)',
          transform: 'translateY(0.5px)',
        };
      }
      props.borderRadius = 4;
      props.textTransform = 'none';
      props.minWidth = 0;
      return props;
    },
  },
  variants: [
    {
      props: {
        color: 'inherit',
        variant: 'contained',
      },
      style({ theme }) {
        return {
          background: theme.palette.grey[800],
          color: theme.palette.grey[50],
          ':hover': {
            color: theme.palette.grey[50],
            background: theme.palette.grey[900],
          },
          ':disabled': {
            background: theme.palette.grey[800],
            color: theme.palette.grey[50],
            opacity: 0.5,
            cursor: 'not-allowed',
            pointerEvents: 'auto',
          },
        };
      },
    },
    {
      props: {
        color: 'inherit',
        variant: 'outlined',
      },
      style({ theme }) {
        return {
          background: 'transparent',
          color: theme.palette.grey[50],
          border: `solid 1px ${theme.palette.grey[700]}`,
          ':hover': {
            borderColor: theme.palette.grey[500],
            background: alpha(theme.palette.grey[50], 0.03),
          },
        };
      },
    },
    {
      props: {
        color: 'inherit',
        variant: 'text',
      },
      style({ theme }) {
        return {
          color: theme.palette.grey[500],
          ':hover': {
            background: alpha(theme.palette.grey[50], 0.03),
          },
        };
      },
    },
  ],
};
